
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button,
    Card, CardContent, IconButton,
    ThemeProvider, createTheme, Avatar,CircularProgress, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useNavigate } from "react-router-dom";
import Tooltip from '@mui/material/Tooltip';
import styled from '@emotion/styled';
import DashboardService from "../dashboard.service";
import AuthService from "../../Authentication/auth.service";
import Alerts from "../../../components/Alerts/alerts";
import AlertService from '../../../components/Alerts/alerts';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CartSummary from './CartSummary';
import EmptyCart from '../../../assets/emptyCart.png';
import { useCart } from '../../../context/CartContext.js';

const theme = createTheme({
    width: '100%',
    maxWidth: '500px',
    palette: {
        primary: {
            main: '#3488A4', // Light blue from the design
            light: '#E3F2FD',
        },
        text: {
            primary: '#37474F',
            secondary: '#78909C',
        },
        background: {
            default: '#FFFFFF',
            paper: '#FFFFFF',
        }
    },
    typography: {
        fontFamily: "'Inter', sans-serif",
        h6: {
            fontSize: '1.25rem',
            fontWeight: 500,
            letterSpacing: 0,
            color: '#3488A4',
        },
    },
});

const QuantityButton = styled(IconButton)(({ theme }) => ({
    width: '32px',
    height: '32px',
    color: '#ffff',
    borderRadius: '50%',
    border: 'none',
    '&:hover': {
        backgroundColor: '#E5E7EB',
    }
}));

const QuantityText = styled(Typography)({
    margin: '0 12px',
    minWidth: '24px',
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: 500,
});

const ItemCard = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    padding: '16px 0',
    borderBottom: '1px solid #E5E7EB',
    '&:last-child': {
        borderBottom: 'none',
    }
});



const MainCard = styled(Card)(({ theme }) => ({
    boxShadow: 'none',
    borderRadius: '8px',
    marginBottom: '16px',
    maxWidth: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
        border: 'none',
    },
}));


const EmptyCard = styled(Card)(({ theme }) => ({
    boxShadow: 'none',
    borderRadius: '8px',
    marginBottom: '16px',
    minHeight: '80vh',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
        borderRadius: 0,
    },
}));


const DeliveryMethodButton = styled(Button)(({ theme, active }) => ({
    flex: 1,
    borderRadius: '20px',
    backgroundColor: active ? theme.palette.primary.main : 'transparent',
    color: active ? 'white' : 'black',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
        backgroundColor: active ? theme.palette.primary.dark : 'rgba(0, 0, 0, 0.04)',
    },
    '& .MuiButton-startIcon': {
        margin: 0,
        marginBottom: '10px',
    },
    '& .MuiButton-startIcon > *:first-of-type': {
        fontSize: '32px',
    },
    [theme.breakpoints.down('sm')]: {
        padding: '10px',
        '& .MuiButton-startIcon > *:first-of-type': {
            fontSize: '24px',
        },
    },
}));

const TipButton = styled(Button)(({ theme, active }) => ({
    minWidth: 0,
    padding: '5px 15px',
    borderRadius: '20px',
    border: `1px solid ${active ? theme.palette.primary.main : '#e0e0e0'}`,
    backgroundColor: active ? theme.palette.primary.main : 'white',
    color: active ? 'white' : 'black',
    [theme.breakpoints.down('sm')]: {
        padding: '3px 10px',
        fontSize: '0.8rem',
    },
}));

const LoaderOverlay = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  right: auto;
  bottom: auto;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background-color: white;
  border-radius: 10px;
  width: 80%;
  max-width: 300px;
  overflow: hidden;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;


const PopupHeader = styled.div`
  background-color: white;
  padding: 20px;
  text-align: center;
`;

const PopupIcon = styled.div`
  background-color: #d32f2f;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto 20px;
`;

const PopupTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const PopupButtons = styled.div`
  display: flex;
`;

const PopupButton = styled.button`
  flex: 1;
  padding: 15px;
  border: none;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:first-of-type {
    background-color: #3488A4;
    color: white;
  }

  &:last-of-type {
    background-color: #c0392b;
    color: white;
  }

  &:hover {
    opacity: 0.9;
  }
`;


const CartView = ({ onClose, showCheckout, setShowCheckout, setShowOrders, setShowProfile, setProduct_Details, handleCategoryClick }) => {
    const [Prioritize, setPrioritize] = useState([]);
    const { cartState, fetchCartData: fetchCartDataGlobal, updateQuantity, removeItem, emptyCart, loading: cartLoading } = useCart();
    const [cartData, setCartData] = useState([]); // kept for backward compatibility; UI will read from cartState
    const [DistanceFormula, setDistanceFormula] = useState(0);
    const [addressesList, setAddressesList] = useState([]);
    const [userDetails, setUserDetails] = useState(null);
    const [userBalance, setUserBalance] = useState(null);
    const [calculations, setCalculations] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [deliveryMethod, setDeliveryMethod] = useState('delivery');
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [priorityTip, setPriorityTip] = useState(0);
    const [couponCode, setCouponCode] = useState('');
    const navigate = useNavigate();
    const [openEmptyCartDialog, setOpenEmptyCartDialog] = useState(false);
    const [RemoveItem, setRemoveItem] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState('');
    const [openAddressDialog, setOpenAddressDialog] = useState(false);
    const [openNewAddressDialog, setOpenNewAddressDialog] = useState(false);
    const [newAddress, setNewAddress] = useState({ label: '', address: '', block: '', unit: '' });
    const [deliveryMethods, setDeliveryMethods] = useState([]);
    const [selectedDeliveryMethod, setSelectedDeliveryMethod] = useState('');

    // Add new state for stock management
    const [productStock, setProductStock] = useState({}); // Track stock for each product
    const [stockWarningShown, setStockWarningShown] = useState({}); // Track which stock warnings have been shown

    // Function to fetch stock information for cart items
    const fetchCartItemStock = async (cartItems) => {
        try {
            //iph todo: 47
            //console.log('🚀 fetchCartItemStock function called!');
            //iph todo: 47
            //console.log('Fetching stock for cart items:', cartItems.length);
            //iph todo: 47
            //console.log('Sample cart item structure:', cartItems[0]);

            if (!cartItems || cartItems.length === 0) {
                //iph todo: 47
                //console.log('❌ No cart items to fetch stock for');
                return;
            }

            // Process items in batches to avoid overwhelming the server
            const BATCH_SIZE = 5;
            for (let i = 0; i < cartItems.length; i += BATCH_SIZE) {
                const batch = cartItems.slice(i, i + BATCH_SIZE);

                //iph todo: 47
                //console.log(`🔄 Processing batch ${Math.floor(i / BATCH_SIZE) + 1}, items ${i + 1} to ${Math.min(i + BATCH_SIZE, cartItems.length)}`);

                const stockPromises = batch.map(async (item) => {
                    try {
                        const product = item.product_details;
                        //iph todo: 47
                        //console.log(`📦 Processing item:`, product);

                        // Validate that product ID exists
                        if (!product?._id) {
                            //iph todo: 47
                            //console.error(`❌ Product ${product?.item_name} has no _id!`);
                            return { productId: product?._id, stock: 0 };
                        }

                        // Get business ID from the cart item
                        const businessId = item.business_details?._id;
                        if (!businessId) {
                            //iph todo: 47
                            //console.error(`❌ No business ID found for product ${product?.item_name}`);
                            return { productId: product._id, stock: 0 };
                        }

                        // Use product._id to fetch stock - backend will get item_code from items collection
                        //iph todo: 47
                        //console.log(`🚀 About to call GetStocks API with product_id: "${product._id}", business: ${businessId}`);
                        //iph todo: 47
                        //console.log(`📡 Making API call to StockModule/GetStocks with product_id parameter...`);
                        //iph todo: 47
                        //console.log(`📋 API payload will be:`, {
                        //     functionName: "GetStocks",
                        //     postData: {
                        //         product_id: product._id,
                        //         business_id: [businessId],
                        //         filterText: "",
                        //         filterStatus: "Active",
                        //         sort: { expiry_date: 1 },
                        //         offset: 0,
                        //         limit: 1000
                        //     }
                        // });

                        // Make direct API call to GetStocks with product_id
                        const axios = (await import('axios')).default;
                        const B_URL = process.env.REACT_APP_LOGINURL;
                        const headers = {
                            'Content-Type': 'application/json',
                            'token': localStorage.getItem('session-id') || ''
                        };

                        const payload = {
                            functionName: "GetStocks",
                            postData: {
                                product_id: product._id,
                                business_id: [businessId],
                                filterText: "",
                                filterStatus: "Active",
                                sort: { expiry_date: 1 },
                                offset: 0,
                                limit: 1000
                            }
                        };

                        const response = await axios.post(B_URL + "StockModule/GetStocks", payload, { headers });
                        //iph todo: 47
                        //console.log(`📥 API response received for ${product.item_name}:`, response);

                        if (response.data && response.data.data && Array.isArray(response.data.data)) {
                            //iph todo: 47
                            //console.log(`Raw API response for ${product.item_name}:`, response.data);

                            // Log all item_codes in the response to see if server filtering worked
                            const responseItemCodes = response.data.data.map(batch => batch.item_code);
                            //iph todo: 47
                            //console.log(`Server returned batches for item_codes: [${responseItemCodes.join(', ')}] when requesting product_id: ${product._id}`);

                            // Calculate total available stock from all batches
                            const totalStock = response.data.data.reduce((sum, batch) => {
                                // Only count active batches with positive quantity
                                if (batch.status === 'Active' && batch.quantity > 0) {
                                    //iph todo: 47
                                    //console.log(`Adding batch ${batch.batch_number}: ${batch.quantity} units for ${product.item_name}`);
                                    return sum + batch.quantity;
                                }
                                return sum;
                            }, 0);

                            //iph todo: 47
                            //console.log(`Total stock for ${product.item_name}: ${totalStock}`);

                            return { productId: product._id, stock: totalStock };
                        } else {
                            //iph todo: 47
                            //console.log(`No stock data found for ${product.item_name}`);
                            //iph todo: 47
                            //console.log('Response structure:', response);
                            return { productId: product._id, stock: 0 };
                        }
                    } catch (error) {
                        console.error(`Error fetching stock for product ${item.product_details?._id}:`, error);
                        console.error('Error details:', {
                            message: error.message,
                            response: error.response?.data,
                            status: error.response?.status
                        });
                        return { productId: item.product_details?._id, stock: 0 };
                    }
                });

                // Wait for current batch to complete
                const batchResults = await Promise.all(stockPromises);

                // Update stock for this batch
                batchResults.forEach(({ productId, stock }) => {
                    if (productId) {
                        //iph todo: 47
                        //console.log(`Setting stock for product ${productId}: ${stock}`);
                        setProductStock(prev => ({
                            ...prev,
                            [productId]: stock
                        }));
                    }
                });

                // Small delay between batches
                await new Promise(resolve => setTimeout(resolve, 200));
            }

            //iph todo: 47
            //console.log('Finished fetching stock for all cart items');
            //iph todo: 47
            //console.log('Final productStock state:', productStock);
        } catch (error) {
            console.error('Error fetching cart item stock:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status
            });
        }
    };

    // Function to check if quantity buttons should be disabled
    const isQuantityButtonDisabled = (productId, currentQuantity, isIncrement) => {
        const currentStock = productStock[productId] || 0;

        if (isIncrement) {
            // Disable increment button if current quantity is already at stock limit
            return currentQuantity >= currentStock;
        } else {
            // Disable decrement button if current quantity is 1 or less
            return currentQuantity <= 1;
        }
    };

    // Function to handle delivery method changes
    const handleDeliveryMethodChange = (event) => {
        const newMethod = event.target.value;
        setSelectedDeliveryMethod(newMethod);
        // You can add additional logic here if needed when delivery method changes
    };

    // Function to handle tip changes
    const handleTip = (tipAmount) => {
        setPriorityTip(tipAmount);
        // You can add additional logic here if needed when tip changes
    };

    // Function to handle address changes
    const handleAddress = async (addressId) => {
        try {
            setLoading(true);
            let status = false;
            if (deliveryMethod !== 'delivery') {
                status = true
            }
            const updateData = {
                "functionName": "GetCartList",
                "postData": {
                    "user_id": userDetails?._id,
                    "filters": {
                        "status": true,
                        "coupan_code": couponCode,
                        "DeliveryMethod": selectedDeliveryMethod,
                        "address_id": addressId,
                        "self_pickup": status,
                        "prioritize": priorityTip,
                        "quantity": {
                            "cart_id": '',
                            "quantity": ''
                        }
                    }
                }
            };
            //iph todo: 47
            //console.log("updateData", updateData);
            DashboardService.GetCartList(updateData).then((response) => {
                setLoading(false); // Set loading state to false after data retrieval
                // Handle response data
                //iph todo: 47
                //console.log("cart Data", response?.data);
                if (response?.data.message === "Unauthorized") {
                    // Perform logout and redirection for unauthorized access
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                }
                else {
                    if (response?.data) {
                        //iph todo: 47
                        //console.log("my updated Item cart Data", response?.data);
                        // setBranches(response?.data.data); // Set total rows based on response length
                        setCartData(response?.data.cartData);
                        setPrioritize(response?.data.Prioritizes);
                        setDistanceFormula(response?.data.DistanceFormula);
                        setAddressesList(response?.data.AddressessList);
                        setUserDetails(response?.data.UserDetails[0]);
                        setUserBalance(response?.data.UserBalance[0]);
                        setCalculations(response?.data.Calculations);

                        // Fetch stock information for updated cart items
                        if (response?.data.cartData && response?.data.cartData.length > 0) {
                            //iph todo: 47
                            //console.log('About to fetch stock for updated cart items:', response?.data.cartData.length);
                            fetchCartItemStock(response?.data.cartData);
                        }
                    }
                }
            },
                (error) => {
                    setLoading(false); // Set loading state to false in case of error
                    const errContent =
                        (error.response && error.response?.data) || error.message || error.toString();
                    //iph todo: 47
                    //console.log("Net:-", errContent)
                    if (errContent) {
                        Alerts.swalErrorAlert(errContent)
                    }
                    else {
                        Alerts.swalErrorAlert("Server Error, Please try again later.")
                    }
                })
        } catch (err) {
            // setError('Failed to fetch cart data. Please try again.');
            console.error('Error fetching cart data:', err);
        } finally {
            // setLoading(false);
        }
    };

    // Function to handle pickup method changes
    const handlePickup = async (pickupMethod) => {
        //iph todo: 47
        //console.log('Applying Self_Pickup:', pickupMethod);
        let status = false;
        if (pickupMethod === 'selfPickUp') {
            status = true;
        }
        try {
            if (status === true) {
                setLoading(true);

                const updateData = {
                    "functionName": "GetCartList",
                    "postData": {
                        "user_id": userDetails?._id,
                        "filters": {
                            "status": true,
                            "coupan_code": "",
                            "delivery_method": "",
                            "prioritize": "",
                            "self_pickup": status,
                            "DeliveryMethod": '',
                            "address_id": '',
                            "quantity": {
                                "cart_id": '',
                                "quantity": ''
                            }
                        }

                    }
                };
                //iph todo: 47
                //console.log("updateData", updateData);
                DashboardService.GetCartList(updateData).then((response) => {
                    setLoading(false); // Set loading state to false after data retrieval
                    // Handle response data
                    //iph todo: 47
                    //console.log("cart Data", response?.data);
                    if (response?.data.message === "Unauthorized") {
                        // Perform logout and redirection for unauthorized access
                        AuthService.logout();
                        navigate("/login");
                        window.location.reload();
                    }
                    else {
                        if (response?.data) {
                            //iph todo: 47
                            //console.log("my updated Item cart Data", response?.data.cartData);
                            // setBranches(response?.data.data); // Set total rows based on response length
                            setCartData(response?.data.cartData);
                            setPrioritize(response?.data.Prioritizes);
                            setDistanceFormula(response?.data.DistanceFormula);
                            setAddressesList(response?.data.AddressessList);
                            setUserDetails(response?.data.UserDetails[0]);
                            setUserBalance(response?.data.UserBalance[0]);
                            setCalculations(response?.data.Calculations);

                            // Fetch stock information for updated cart items
                            if (response?.data.cartData && response?.data.cartData.length > 0) {
                                //iph todo: 47
                                //console.log('About to fetch stock for updated cart items:', response?.data.cartData.length);
                                fetchCartItemStock(response?.data.cartData);
                            }
                        }
                    }
                },
                    (error) => {
                        setLoading(false); // Set loading state to false in case of error
                        const errContent =
                            (error.response && error.response?.data) || error.message || error.toString();
                        //iph todo: 47
                        //console.log("Net:-", errContent)
                        if (errContent) {
                            Alerts.swalErrorAlert(errContent)
                        }
                        else {
                            Alerts.swalErrorAlert("Server Error, Please try again later.")
                        }
                    })
            } else {
                fetchCartData();
            }
        } catch (err) {
            // setError('Failed to fetch cart data. Please try again.');
            console.error('Error fetching cart data:', err);
        } finally {
            // setLoading(false);
        }
    };

    const handleOpenAddressDialog = () => {
        setOpenAddressDialog(true);
    };

    const handleCloseAddressDialog = () => {
        setOpenAddressDialog(false);
    };

    const handleOpenNewAddressDialog = () => {
        setOpenNewAddressDialog(true);
        setOpenAddressDialog(false);
    };

    const handleCloseNewAddressDialog = () => {
        setOpenNewAddressDialog(false);
        setOpenAddressDialog(true);
    };

    const handleAddNewAddress = (newAddress) => {
        setAddressesList(prevAddresses => {
            const updatedAddresses = [...prevAddresses, newAddress];
            // Automatically select the newly added address
            handleSelectAddress(newAddress?._id);
            return updatedAddresses;
        });
        handleCloseAddressDialog();
    };

    const handleSelectAddress = (addressId) => {
        setSelectedAddress(addressId);
        setDeliveryAddress(addressId);
        handleAddress(addressId);
        handleCloseAddressDialog();
    };

    useEffect(() => {
        fetchCartDataGlobal();
    }, [fetchCartDataGlobal]);

    // Monitor productStock changes for debugging
    useEffect(() => {
        //iph todo: 47
        //console.log('🔄 productStock state changed:', productStock);
        //iph todo: 47
        //console.log('📊 Current stock summary:', Object.entries(productStock).map(([productId, stock]) => ({ productId, stock })));
    }, [productStock]);

    // Log initial state
    useEffect(() => {
        //iph todo: 47
        //console.log('🚀 CartView component mounted');
        //iph todo: 47
        //console.log('📋 Initial productStock state:', productStock);
    }, []);

    // When global cart updates, fetch stock for the visible items
    useEffect(() => {
        if (cartState?.cartData && cartState.cartData.length > 0) {
            fetchCartItemStock(cartState.cartData);
        }
    }, [cartState?.cartData]);

    const fetchCartData = async () => {
        try {
            setLoading(true);
            let status = false;
            if (deliveryMethod !== 'delivery') {
                status = true
            }
            const userString = localStorage.getItem('user');
            const user = JSON.parse(userString);
            const userId = user['_id'];
            const updateData = {
                "functionName": "GetCartList",
                "postData": {
                    "user_id": userId,
                    "filters": {
                        "status": false,
                        "coupan_code": '',
                        "prioritize": "",
                        "DeliveryMethod": '',
                        "address_id": '',
                        "self_pickup": status,
                        "quantity": {
                            "cart_id": '',
                            "quantity": ''
                        }
                    }

                }
            };
            DashboardService.GetCartList(updateData).then((response) => {
                setLoading(false); // Set loading state to false after data retrieval
                // Handle response data
                //iph todo: 47
                //console.log("cart Data", response?.data);
                if (response?.data.message === "Unauthorized") {
                    // Perform logout and redirection for unauthorized access
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                }
                else {
                    if (response?.data) {
                        setPrioritize(response?.data.Prioritizes);
                        setCartData(response?.data.cartData);
                        setDeliveryMethods(response?.data.DeliveryMethods);
                        if (response?.data?.DeliveryMethods?.length > 0 && !selectedDeliveryMethod) {
                            setSelectedDeliveryMethod(response?.data?.DeliveryMethods?.[0]?._id);
                        }
                        setDistanceFormula(response?.data.DistanceFormula);
                        setAddressesList(response?.data.AddressessList);
                        setUserDetails(response?.data.UserDetails?.[0]);
                        setUserBalance(response?.data.UserBalance?.[0]);
                        setCalculations(response?.data.Calculations);

                        // Fetch stock information for cart items
                        if (response?.data.cartData && response?.data.cartData.length > 0) {
                            //iph todo: 47
                            //console.log('About to fetch stock for cart items:', response?.data.cartData.length);
                            fetchCartItemStock(response?.data.cartData);
                        }
                    }
                }
            },
                (error) => {
                    setLoading(false); // Set loading state to false in case of error
                    const errContent =
                        (error.response && error.response?.data) || error.message || error.toString();
                    //iph todo: 47
                    //console.log("Net:-", errContent)
                    if (errContent) {
                        Alerts.swalErrorAlert(errContent)
                    }
                    else {
                        Alerts.swalErrorAlert("Server Error, Please try again later.")
                    }
                })
        } catch (err) {
            // setError('Failed to fetch cart data. Please try again.');
            console.error('Error fetching cart data:', err);
        } finally {
            // setLoading(false);
        }
    };


    const handleUpdateQuantity = (Item, newQuantity, method) => {
        if (newQuantity === 0) {
            handleOpenRemoveItemPopup(Item)
        } else {
            // Check stock limits before updating quantity
            const currentStock = productStock[Item.product_details?._id] || 0;
            const currentQuantity = parseInt(Item.quantity) || 0;

            //iph todo: 47
            //console.log(`handleUpdateQuantity stock check for product ${Item.product_details?._id}:`, {
            //     requestedQuantity: newQuantity,
            //     currentQuantity,
            //     currentStock,
            //     wouldExceed: newQuantity > currentStock
            // });

            // Check if the Requested Qty would exceed available stock
            if (newQuantity > currentStock) {
                // Prevent showing the same warning multiple times
                if (!stockWarningShown[Item.product_details?._id]) {
                    //iph todo: 47
                    //console.log(`Showing stock warning: Only ${currentStock} items available`);
                    AlertService.swalInfoAlert(`Only ${currentStock} items available in stock.`);
                    // Mark this warning as shown for this product
                    setStockWarningShown(prev => ({
                        ...prev,
                        [Item.product_details?._id]: true
                    }));
                    // Reset the warning after 5 seconds
                    setTimeout(() => {
                        setStockWarningShown(prev => ({
                            ...prev,
                            [Item.product_details?._id]: false
                        }));
                    }, 5000);
                }
                return; // Don't proceed with the update
            }

            handleUpdateItem(Item, method)
        }
    };


    const handleApplyCoupon = async () => {
        setLoading(true);
        //iph todo: 47
        //console.log('Applying coupon:', couponCode);
        let status = false;
        if (deliveryMethod !== 'delivery') {
            status = true
        }
        try {
            const updateData = {
                "functionName": "GetCartList",
                "postData": {
                    "user_id": userDetails?._id,
                    "filters": {
                        "status": true,
                        "coupan_code": couponCode,
                        "DeliveryMethod": selectedDeliveryMethod,
                        "address_id": deliveryAddress,
                        "self_pickup": status,
                        "prioritize": priorityTip,
                        "quantity": {
                            "cart_id": '',
                            "quantity": ''
                        }
                    }

                }
            };
            //iph todo: 47
            //console.log("updateData", updateData);
            DashboardService.GetCartList(updateData).then((response) => {
                setLoading(false);
                // Handle response data
                //iph todo: 47
                //console.log("cart Data", response?.data);
                if (response?.data.message === "Unauthorized") {
                    // Perform logout and redirection for unauthorized access
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                }
                else {
                    if (response?.data) {
                        //iph todo: 47
                        //console.log("my cart Data", response?.data.cartData);
                        setCartData(response?.data.cartData);
                        setPrioritize(response?.data.Prioritizes);
                        setDistanceFormula(response?.data.DistanceFormula);
                        setAddressesList(response?.data.AddressessList);
                        setUserDetails(response?.data.UserDetails[0]);
                        setUserBalance(response?.data.UserBalance[0]);
                        setCalculations(response?.data.Calculations);

                        // Fetch stock information for updated cart items
                        if (response?.data.cartData && response?.data.cartData.length > 0) {
                            //iph todo: 47
                            //console.log('About to fetch stock for updated cart items:', response?.data.cartData.length);
                            fetchCartItemStock(response?.data.cartData);
                        }
                    }
                }
            },
                (error) => {
                    setLoading(false); // Set loading state to false in case of error
                    const errContent =
                        (error.response && error.response?.data) || error.message || error.toString();
                    //iph todo: 47
                    //console.log("Net:-", errContent)
                    if (errContent) {
                        Alerts.swalErrorAlert(errContent)
                    }
                    else {
                        Alerts.swalErrorAlert("Server Error, Please try again later.")
                    }
                })
        } catch (err) {
            // setError('Failed to fetch cart data. Please try again.');
            console.error('Error fetching cart data:', err);
        } finally {
            // setLoading(false);
        }
    };

    const handleUpdateItem = async (item, method) => {
        //iph todo: 47
        //console.log('Applying update item:', item);

        // Check stock limits before updating
        const currentStock = productStock[item.product_details?._id] || 0;
        let newQuantity;

        if (method === 'add') {
            newQuantity = parseInt(item.quantity) + 1;
        } else {
            newQuantity = parseInt(item.quantity) - 1;
        }

        // Check if the new quantity would exceed available stock
        if (newQuantity > currentStock) {
            // Prevent showing the same warning multiple times
            if (!stockWarningShown[item.product_details?._id]) {
                //iph todo: 47
                //console.log(`Showing stock warning in handleUpdateItem: Only ${currentStock} items available`);
                AlertService.swalInfoAlert(`Only ${currentStock} items available in stock.`);
                // Mark this warning as shown for this product
                setStockWarningShown(prev => ({
                    ...prev,
                    [item.product_details?._id]: true
                }));
                // Reset the warning after 5 seconds
                setTimeout(() => {
                    setStockWarningShown(prev => ({
                        ...prev,
                        [item.product_details?._id]: false
                    }));
                }, 5000);
            }
            return; // Don't proceed with the update
        }

        // Update via global cart context
        await updateQuantity(item._id, newQuantity, {
            self_pickup: true,
            coupan_code: couponCode,
            prioritize: priorityTip,
        });
    };

    const handleRemoveItem = async () => {
        //iph todo: 47
        //console.log('Applying Remove item:', RemoveItem);
        setShowRemoveItemPopup(false);
        await removeItem(RemoveItem?._id, {
            self_pickup: true,
            coupan_code: couponCode,
            prioritize: priorityTip,
        });
    };

    const [showEmptyCartPopup, setShowEmptyCartPopup] = useState(false);
    const [showRemoveItemPopup, setShowRemoveItemPopup] = useState(false);

    const handleOpenRemoveItemPopup = (item) => {
        setShowRemoveItemPopup(true);
        setRemoveItem(item)
    };

    const handleCloseRemoveItemPopup = () => {
        setShowRemoveItemPopup(false);
    };

    const handleOpenEmptyCartPopup = () => {
        setShowEmptyCartPopup(true);
    };

    const handleCloseEmptyCartPopup = () => {
        setShowEmptyCartPopup(false);
    };



    const HandleCartEmpty = async () => {
        setShowEmptyCartPopup(false);
        AlertService.successToast("Cart emptied successfully");
        await emptyCart();
    };


    const HandlePlaceOrder = async (cartData, deliveryMethod, deliveryAddress, priorityTip, couponCode, total) => {
        //iph todo: 47
        //console.log('cartData----->', cartData);
        try {
            let Cart = []
            cartData.map((cartItem) => {
                Cart.push({
                    _id: cartItem._id,
                    quantity: cartItem.quantity,
                    price: cartItem.price,
                    total_price: cartItem.total_price,
                    category_id: cartItem.category_details._id,
                    business_id: cartItem.business_details._id,
                    product_id: cartItem.product_details._id,
                    variations: cartItem.variations,
                    is_reviewed: 0,
                })
            })
            const userString = localStorage.getItem('user');
            const user = JSON.parse(userString);
            const userId = user['_id'];
            let Addresses = [];
            if (deliveryMethod !== 'selfPickUp') {
                Addresses = addressesList.filter(address =>
                    address._id === deliveryAddress
                );
            }

            let pay_topup = false;
            //iph todo: 47
            //console.log("userBalance?.total_balance", userBalance?.total_balance);
            if (userBalance?.total_balance > total) {
                let userRemainingBalance = userBalance?.total_balance - total
                if (userRemainingBalance >= 0) {
                    //iph todo: 47
                    //console.log("Pay From Topup");
                    pay_topup = true;
                }
            }

            setLoading(true);
            var getData = {
                functionName: "placeOrder",
                "postData": {
                    user_id: userId,
                    pay_topup: pay_topup,
                    cartData: Cart,
                    deliveryMethod,
                    Addresses,
                    priorityTip,
                    couponCode,
                    total: parseFloat(total).toFixed(2),
                    DistanceFormula: DistanceFormula,
                    Calculations: calculations
                }
            }
            //iph todo: 47
            //console.log("getData", getData);
            setLoading(true);
            DashboardService.PlaceOrder(getData).then((response) => {
                //iph todo: 47
                //console.log(response);
                setLoading(false);
                if (response?.status === 200) {
                    //iph todo: 47
                    //console.log(response);
                    if (pay_topup) {
                        // Handle pay_topup case
                    } else {
                        window.open(response?.data.data, '_blank');
                        window.location.reload();
                    }
                    AlertService.successToast("Order Placed Now Pay to Proceed.")
                    fetchCartData();
                } else if (response?.status === 401) {
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                } else {
                    window.open(response?.data.data, '_blank');
                }
            },
                (error) => {
                    setLoading(false);
                    const errContent =
                        (error.response && error.response?.data) || error.message || error.toString();
                    //iph todo: 47
                    //console.log("Net:-", errContent);
                    if (errContent) {
                        Alerts.swalErrorAlert(errContent);
                    } else {
                        Alerts.swalErrorAlert("Server Error, Please try again later.");
                    }
                })
        } catch (err) {
            console.error('Error placing order:', err);
        } finally {
            setLoading(false);
        }
    };


    const renderSelectedAddress = () => {
        if (!deliveryAddress) return null;

        const selectedAddress = addressesList.find(a => a._id === deliveryAddress);
        if (!selectedAddress) return null;

        return (
            <Box sx={{ borderBottom: '1px solid #E2E8F0' }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                }}>
                    <Typography variant="subtitle1">Deliver To</Typography>
                    <Button
                        startIcon={<EditIcon />}
                        sx={{
                            color: '#3488A4',
                            textTransform: 'none',
                            fontWeight: 500
                        }}
                        onClick={handleOpenAddressDialog}
                    >
                        Edit Address
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <LocationOnIcon sx={{ color: '#3488A4' }} />
                    <Box>
                        <Typography variant="body1" sx={{ mb: 1, color: '#1E293B' }}>
                            {selectedAddress?.location}
                        </Typography>
                        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DirectionsBikeIcon sx={{ color: '#3488A4', fontSize: '1rem' }} />
                            <Typography variant="body2" color="text.secondary">
                                Distance from you: {DistanceFormula?.distance?.value
                                    ? `${(parseFloat(DistanceFormula.distance.value) / 1000).toFixed(2)}km (${calculations.deliveryTipFee.toFixed(2)})`
                                    : ''}
                            </Typography>
                        </Box> */}
                    </Box>
                </Box>
            </Box>
        );
    };


    return (
        <ThemeProvider theme={theme}>
            <Box sx={{
                maxWidth: 480,
                width: '100%',
                margin: 'auto',
                backgroundColor: 'white',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: '1px solid #E2E8F0'
                }}>
                    <IconButton
                        onClick={onClose}
                        sx={{ mr: 2 }}
                    >
                        <CloseIcon sx={{ color: '#64748B' }} />
                    </IconButton>
                    <Typography
                        variant="h6"
                        sx={{
                            color: '#3488A4',
                            flexGrow: 1,
                            fontSize: '1.25rem',
                            fontWeight: 500
                        }}
                    >
                        Cart
                    </Typography>
                    <IconButton onClick={handleOpenEmptyCartPopup}>
                        <DeleteOutlineIcon sx={{ color: '#64748B' }} />
                    </IconButton>
                </Box>
                {showEmptyCartPopup && (
                    <PopupOverlay>
                        <PopupContent>
                            <PopupHeader>
                                <PopupIcon>
                                    <span style={{ color: 'white', fontSize: '30px' }}>?</span>
                                </PopupIcon>
                                <PopupTitle>Remove all cart items?</PopupTitle>
                            </PopupHeader>
                            <PopupButtons>
                                <PopupButton onClick={handleCloseEmptyCartPopup}>No</PopupButton>
                                <PopupButton onClick={HandleCartEmpty}>Yes</PopupButton>
                            </PopupButtons>
                        </PopupContent>
                    </PopupOverlay>
                )}

                {showRemoveItemPopup && (
                    <PopupOverlay>
                        <PopupContent>
                            <PopupHeader>
                                <PopupIcon>
                                    <span style={{ color: 'white', fontSize: '30px' }}>?</span>
                                </PopupIcon>
                                <PopupTitle>Remove cart item?</PopupTitle>
                            </PopupHeader>
                            <PopupButtons>
                                <PopupButton onClick={handleCloseRemoveItemPopup}>No</PopupButton>
                                <PopupButton onClick={() => { handleRemoveItem() }}>Yes</PopupButton>
                            </PopupButtons>
                        </PopupContent>
                    </PopupOverlay>
                )}


                {(loading || cartLoading) && (
                    <LoaderOverlay>
                        <CircularProgress />
                    </LoaderOverlay>
                )}
                {error ? (
                    <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : cartState?.cartData && cartState.cartData.length > 0 ? (
                    <MainCard  >
                        {/* <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <DeliveryMethodButton
                                    active={deliveryMethod === 'delivery'}
                                    onClick={() => {
                                        setDeliveryMethod('delivery');
                                        handlePickup('delivery');
                                    }}
                                    startIcon={<img src={deliveryIcon} alt="Delivery" width="50" height="50" />}
                                >
                                    Delivery
                                </DeliveryMethodButton>
                                <DeliveryMethodButton
                                    active={deliveryMethod === 'selfPickUp'}
                                    onClick={() => {
                                        setDeliveryMethod('selfPickUp');
                                        handlePickup('selfPickUp');
                                    }}
                                    startIcon={<img src={pickup} alt="Self Pickup" width="50" height="50" />}
                                >
                                    Self Pickup
                                </DeliveryMethodButton>
                            </Box>
                            {deliveryMethod === 'delivery' && (
                                <>
                                    <Typography variant="subtitle2" mb={1}>Delivery Method</Typography>
                                    {deliveryMethods?.length === 0 ?
                                        <Typography color='red' variant="subtitle2" mb={1}>No Delivery Method available right now Please choose self pickup</Typography>
                                        :
                                        <FormControl fullWidth sx={{ mb: 2 }}>
                                            <InputLabel id="delivery-method-label">Delivery Method</InputLabel>
                                            <Select
                                                labelId="delivery-method-label"
                                                id="delivery-method-select"
                                                value={selectedDeliveryMethod}
                                                label="Delivery Method"
                                                onChange={handleDeliveryMethodChange}
                                            >
                                                {deliveryMethods?.map((method) => (
                                                    <MenuItem key={method._id} value={method._id}>
                                                        {method.method === 'flatShipment' ? "Flat Shipment" : method.method === 'lalamove' ? "Lalamove" : method.method}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    }
                                    {deliveryAddress ? (
                                        renderSelectedAddress()
                                    ) : (
                                        <>
                                            <Typography variant="subtitle1" gutterBottom>Deliver To</Typography>
                                            <Box
                                                onClick={handleOpenAddressDialog}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    p: 2,
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <LocationOnIcon sx={{ color: '#3488A4', mr: 2 }} />
                                                <Typography sx={{ flexGrow: 1, color: deliveryAddress ? 'black' : 'grey' }}>
                                                    {deliveryAddress
                                                        ? addressesList.find(a => a._id === deliveryAddress)?.label
                                                        : 'Select your Address'}
                                                </Typography>
                                                <IconButton>
                                                    <AddIcon />
                                                </IconButton>
                                            </Box>
                                        </>
                                    )}
                                    <AddressPopup
                                        key={addressesList?.length}
                                        open={openAddressDialog}
                                        onClose={handleCloseAddressDialog}
                                        addresses={addressesList}
                                        onSelectAddress={(address) => {
                                            handleSelectAddress(address._id);
                                            handleCloseAddressDialog();
                                        }}
                                        onAddNew={handleAddNewAddress}
                                    />

                                    <Dialog open={openNewAddressDialog} onClose={handleCloseNewAddressDialog} fullWidth>
                                        <DialogTitle>
                                            Add New Address
                                            <IconButton
                                                aria-label="close"
                                                onClick={handleCloseNewAddressDialog}
                                                sx={{ position: 'absolute', right: 8, top: 8 }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </DialogTitle>
                                        <DialogContent>
                                            <TextField
                                                label="Search Nearest Location"
                                                fullWidth
                                                margin="normal"
                                                value={newAddress.address}
                                                onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                                                InputProps={{
                                                    startAdornment: <LocationOnIcon />,
                                                }}
                                            />
                                            <TextField
                                                label="Block or Street name"
                                                fullWidth
                                                margin="normal"
                                                value={newAddress.block}
                                                onChange={(e) => setNewAddress({ ...newAddress, block: e.target.value })}
                                            />
                                            <TextField
                                                label="Unit/House no."
                                                fullWidth
                                                margin="normal"
                                                value={newAddress.unit}
                                                onChange={(e) => setNewAddress({ ...newAddress, unit: e.target.value })}
                                            />
                                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Label as</Typography>
                                            <Select
                                                value={newAddress.label}
                                                onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                                                fullWidth
                                            >
                                                <MenuItem value="Home">Home</MenuItem>
                                                <MenuItem value="Work">Work</MenuItem>
                                                <MenuItem value="Other">Other</MenuItem>
                                            </Select>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleCloseNewAddressDialog}>Cancel</Button>
                                            <Button onClick={handleAddNewAddress} variant="contained">Add</Button>
                                        </DialogActions>
                                    </Dialog>

                                    <Box sx={{ p: 2, backgroundColor: 'white', borderRadius: 2 }}>
                                        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Prioritize Your Delivery</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 2, display: 'block' }}>
                                            *Tip helps to prioritize your delivery
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2 }}>

                                            {Prioritize?.map((tip) => (
                                                <Button
                                                    key={tip}
                                                    variant={priorityTip === tip ? 'contained' : 'outlined'}
                                                    onClick={() => {
                                                        handleTip(priorityTip === tip ? 0 : tip);
                                                        setPriorityTip(priorityTip === tip ? 0 : tip);
                                                    }}
                                                    sx={{ borderRadius: '20px', minWidth: '60px' }}
                                                >
                                                    {parseFloat(tip).toFixed(2)}
                                                </Button>
                                            ))}
                                        </Box>

                                    </Box>
                                </>
                            )}
                        </CardContent> */}
                        <CardContent sx={{ background: 'white', boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)' }}>
                            <Typography sx={{ fontSize: '20px', fontFamily: 'Quick-Sand' }} variant="subtitle1" gutterBottom>Order Summary</Typography>
                            {cartState?.cartData?.map((item) => (
                                <ItemCard key={item._id}>
                                    <Avatar
                                        src={item?.product_details?.Images?.[0]}
                                        variant="rounded"
                                        sx={{
                                            width: 64,
                                            height: 64,
                                            borderRadius: '8px',
                                            mr: 2
                                        }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                                        {/* Name and stock vertically stacked, left-aligned, next to image */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                            <Tooltip title={item?.product_details?.item_name || ''} arrow>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontSize: '16px',
                                                        fontWeight: 500,
                                                        mb: 0.3,
                                                        whiteSpace: 'nowrap',
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        maxWidth: '180px',
                                                        display: 'block',
                                                        textAlign: 'left',
                                                        cursor: item?.product_details?.item_name?.length > 25 ? 'pointer' : 'default',
                                                    }}
                                                >
                                                    {item?.product_details?.item_name}
                                                </Typography>
                                            </Tooltip>
                                            {/* Stock indicator - only show Out of Stock or Limited stock; keep fixed height for alignment */}
                                            <Box sx={{ mt: 0, mb: 0.3, minHeight: 18, display: 'flex', alignItems: 'center' }}>
                                                {(() => {
                                                    const s = productStock[item.product_details?._id];
                                                    if (s === 0) {
                                                        return (
                                                            <Typography variant="caption" sx={{
                                                                color: '#f44336',
                                                                backgroundColor: '#fdecea',
                                                                border: '1px solid #f44336',
                                                                borderRadius: '4px',
                                                                padding: '1px 6px',
                                                                fontWeight: 600,
                                                                lineHeight: '16px'
                                                            }}>Out of Stock</Typography>
                                                        );
                                                    }
                                                    if (typeof s === 'number' && s > 0 && s < 30) {
                                                        return (
                                                            <Typography variant="caption" sx={{
                                                                color: '#ff6b35',
                                                                backgroundColor: '#fff3e0',
                                                                border: '1px solid #ff6b35',
                                                                borderRadius: '4px',
                                                                padding: '1px 6px',
                                                                fontWeight: 600,
                                                                lineHeight: '16px'
                                                            }}>Limited stock</Typography>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </Box>
                                        </Box>
                                        {item?.variations && item?.variations.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>Variations:</Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {item?.variations.map((variation, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={`${variation.variation_name}: ${variation.variation_type_name} (RM${parseFloat(variation.variation_price).toFixed(2)} X ${item.quantity})`}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: theme.palette.primary.dark,
                                                                color: theme.palette.primary.contrastText,
                                                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                    }}>
                                        <Box sx={{ alignSelf: 'flex-end', mb: 1 }}>
                                            <IconButton size="small" onClick={() => handleOpenRemoveItemPopup(item)} sx={{ p: 0.5 }}>
                                                <DeleteOutlineIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            ml: 2
                                        }}>
                                            <QuantityButton
                                                sx={{
                                                    backgroundColor: '#3389A4',
                                                    opacity: isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), false) ? 0.5 : 1,
                                                    cursor: isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), false) ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => handleUpdateQuantity(item, parseInt(item?.quantity) - 1, 'subtract')}
                                                disabled={isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), false)}
                                            >
                                                <RemoveIcon fontSize="small" />
                                            </QuantityButton>
                                            <Typography sx={{ ml: '5px', mr: '5px', color: 'black', fontSize: '22', fontFamily: 'Quicksand', mx: 1 }}>{item?.quantity} </Typography>
                                            <QuantityButton
                                                sx={{
                                                    backgroundColor: '#3D5F7A',
                                                    opacity: isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), true) ? 0.5 : 1,
                                                    cursor: isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), true) ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => handleUpdateQuantity(item, parseInt(item?.quantity) + 1, 'add')}
                                                disabled={isQuantityButtonDisabled(item.product_details?._id, parseInt(item?.quantity), true)}
                                            >
                                                <AddIcon fontSize="small" />
                                            </QuantityButton>
                                        </Box>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            ml: 2
                                        }}>
                                            <Typography sx={{ color: '#3389A4', fontSize: '42', fontFamily: 'Quicksand', fontWeight: '700', marginTop: '3px' }}>RM{parseFloat(parseFloat(item?.price).toFixed(2) * parseFloat(item?.quantity).toFixed(2)).toFixed(2)}</Typography>
                                        </Box>
                                    </Box>
                                </ItemCard>
                            ))}
                        </CardContent>

                        <CartSummary
                            onClose={onClose}
                            setShowOrders={setShowOrders}
                            setShowProfile={setShowProfile}
                            setProduct_Details={setProduct_Details}
                            handleCategoryClick={handleCategoryClick}
                            calculations={cartState?.Calculations || {}}
                            userBalance={cartState?.UserBalance || userBalance}
                            cartData={cartState?.cartData || []}
                            setShowCheckout={setShowCheckout}
                            showCheckout={showCheckout}
                            couponCode={couponCode}
                            setCouponCode={setCouponCode}
                            handleApplyCoupon={handleApplyCoupon}
                            HandlePlaceOrder={HandlePlaceOrder}
                            deliveryAddress={deliveryAddress}
                            deliveryMethod={deliveryMethod}
                            priorityTip={priorityTip}
                        />
                    </MainCard>
                ) : (
                    <EmptyCard>
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" flexGrow={1} p={2}>
                            <img alt='' src={EmptyCart} style={{ maxWidth: '100%', height: 'auto' }} />
                            <Typography variant="h5" align="center" gutterBottom>Your Cart is Empty</Typography>
                            <Typography variant="body1" align="center">
                                Looks like you haven't added any items to your cart yet.
                            </Typography>
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                sx={{ mt: 3, py: 1.5, fontSize: '1.1rem', maxWidth: 300, borderRadius: '20px', color: 'white' }}
                                onClick={() => {
                                    setShowOrders(false);
                                    setShowProfile(false);
                                    setProduct_Details(null);
                                    handleCategoryClick(null);
                                    onClose()
                                }}
                            >
                                Start Shopping
                            </Button>
                        </Box>
                    </EmptyCard>
                )}


            </Box>
        </ThemeProvider>
    );
};

export default CartView;